import {
  boolean,
  email,
  maxLength,
  nonEmpty,
  nonNullable,
  nullable,
  number,
  object,
  picklist,
  pipe,
  record,
  string,
  trim,
} from 'valibot';

import { DATETIME_FORMATS } from '@constants/datetimeFormats';
import { simpleEventsKeys } from '@constants/simpleEventsKeys';

import { getSimpleEmailSubscriptions } from '@helpers/getSimpleEmailSubscriptions';

import type { ILanguage } from '@model/language';
import type { ITimezone } from '@model/timezone';
import type { IFullUser, ISimpleEventDictionary } from '@model/userFullProfile';

export const PROFILE_FIELDS = {
  NAME_FIRST: 'name_first',
  NAME_LAST: 'name_last',
  USERNAME: 'username',
  COMPANY: 'company',
  JOB_TITLE: 'job_title',
  PHONE: 'phone',
  EMAIL: 'email',
  IMAGE: 'image',
  SECURITY_EMAIL: 'security_email',
  TIMEZONE: 'timezone_id',
  LOCATIONS: 'locations',
  LANGUAGE: 'language_iso_code',
  USE_METRIC: 'use_metric',
  UNITS: 'units',
  DATETIME_FORMAT: 'datetime_format',
  ACTION_LIST_URGENCIES: 'action_list_urgencies',
  DEFAULT_SUBSCRIBED: 'default_subscribed_to_email_subscription_settings',
  RECEIVE_OWN_EMAILS: 'receive_own_emails',
  REFRESH_INTERVAL: 'refresh_interval',
  REMOVE_IMAGE: 'removeImage',
  SIMPLE_EMAIL_SUBSCRIPTIONS: 'simple_email_subscriptions',
};

export const schemaProfile = object({
  [PROFILE_FIELDS.NAME_FIRST]: pipe(string(), trim(), nonEmpty('This field is required.')),
  [PROFILE_FIELDS.NAME_LAST]: pipe(string(), trim(), nonEmpty('This field is required.')),
  [PROFILE_FIELDS.EMAIL]: pipe(
    string(),
    trim(),
    nonEmpty('This field is required.'),
    email('Enter correct email'),
    maxLength(255, 'Your email is too long.'),
  ),
  [PROFILE_FIELDS.USERNAME]: pipe(string(), trim(), nonEmpty('This field is required.')),
  [PROFILE_FIELDS.LANGUAGE]: object({
    iso_code: nonNullable(nullable(string()), 'This field is required.'),
    name: string(),
  }),
  [PROFILE_FIELDS.COMPANY]: pipe(string(), trim(), nonEmpty('This field is required.')),
  [PROFILE_FIELDS.JOB_TITLE]: string(),
  [PROFILE_FIELDS.PHONE]: string(),
  [PROFILE_FIELDS.IMAGE]: nullable(string()),
  [PROFILE_FIELDS.REMOVE_IMAGE]: boolean(),
  [PROFILE_FIELDS.TIMEZONE]: object({
    id: nonNullable(nullable(number()), 'This field is required.'),
    title: string(),
  }),
  [PROFILE_FIELDS.REFRESH_INTERVAL]: object({ value: number() }),
  [PROFILE_FIELDS.USE_METRIC]: number('This field is required.'),
  [PROFILE_FIELDS.DATETIME_FORMAT]: pipe(string(), nonEmpty('This field is required.')),
  [PROFILE_FIELDS.ACTION_LIST_URGENCIES]: object({ 1: boolean(), 2: boolean(), 3: boolean() }),
  [PROFILE_FIELDS.DEFAULT_SUBSCRIBED]: boolean(),
  [PROFILE_FIELDS.RECEIVE_OWN_EMAILS]: boolean(),
  [PROFILE_FIELDS.SIMPLE_EMAIL_SUBSCRIPTIONS]: record(picklist(simpleEventsKeys), boolean()),
});

export const profileDefaultValues = (
  t: (text: string, fields?: unknown, isMatch?: boolean) => string,
  reloadTimeoutList: Array<{ value: number; name: string }>,
  refreshInterval: number,
  profileData?: IFullUser,
  simpleEventsDictionary?: Array<ISimpleEventDictionary>,
  languageListData?: Array<ILanguage>,
  timezoneList?: Array<ITimezone>,
) => {
  const currentLanguage: any = languageListData?.find(
    (lang) => lang?.iso_code === profileData?.language_iso_code,
  );
  const currentTimezone = timezoneList?.find((zone) => zone?.id === profileData?.timezone_id);

  const getAIUrgencies = () => {
    const aiUrgencies: { [key: number]: boolean } = { 1: false, 2: false, 3: false };

    profileData?.dashboard_push_setting_urgencies.forEach((urgency) => {
      aiUrgencies[urgency.id] = true;
    });

    return aiUrgencies;
  };

  const reloadTimeoutValue = reloadTimeoutList.find((item) => +item.value === +refreshInterval);

  return {
    [PROFILE_FIELDS.NAME_FIRST]: profileData?.name_first || '',
    [PROFILE_FIELDS.NAME_LAST]: profileData?.name_last || '',
    [PROFILE_FIELDS.USERNAME]: profileData?.username || '',
    [PROFILE_FIELDS.COMPANY]: profileData?.company || '',
    [PROFILE_FIELDS.JOB_TITLE]: profileData?.job_title || '',
    [PROFILE_FIELDS.PHONE]: profileData?.phone || '',
    [PROFILE_FIELDS.EMAIL]: profileData?.email || '',
    [PROFILE_FIELDS.IMAGE]: profileData?.userpic || null,
    [PROFILE_FIELDS.REMOVE_IMAGE]: false,
    [PROFILE_FIELDS.SECURITY_EMAIL]: profileData?.security_email || '',
    [PROFILE_FIELDS.TIMEZONE]: currentTimezone || {
      id: 21,
      title: t('(UTC-05:00) Eastern Time (US & Canada)'),
    },
    [PROFILE_FIELDS.LANGUAGE]: currentLanguage || { iso_code: 'en', name: t('English') },
    [PROFILE_FIELDS.REFRESH_INTERVAL]: reloadTimeoutValue,
    [PROFILE_FIELDS.USE_METRIC]: profileData?.id ? profileData?.use_metric : 1,
    [PROFILE_FIELDS.UNITS]: profileData?.personal_settings?.units || [],
    [PROFILE_FIELDS.DATETIME_FORMAT]:
      profileData?.personal_settings?.datetime_format || DATETIME_FORMATS.us.dateTime,
    [PROFILE_FIELDS.ACTION_LIST_URGENCIES]: getAIUrgencies(),
    [PROFILE_FIELDS.DEFAULT_SUBSCRIBED]: profileData?.id
      ? !!profileData?.default_subscribed_to_email_subscription_settings
      : true,
    [PROFILE_FIELDS.RECEIVE_OWN_EMAILS]: profileData?.id ? !!profileData?.receive_own_emails : 1,
    [PROFILE_FIELDS.SIMPLE_EMAIL_SUBSCRIPTIONS]: getSimpleEmailSubscriptions(
      simpleEventsDictionary,
      profileData?.email_subscriptions,
    ),
  };
};
